import { useAppSelector } from '@/app/hooks';
import { selectIsSharedTableCart, selectTableSession } from './tableSessionSlice';
import { useGetTableSessionQuery, type SharedCartLine } from './tableSessionApi';
import { useTableSessionPollInterval } from './useTableSessionPollInterval';

export interface SharedTableCartData {
  /** True only in dine-in ordering mode with the shared-cart flag on. */
  isShared: boolean;
  guestToken: string;
  /** This device's guest uuid (decoded from the guest JWT) — flags own group. */
  guestUuid: string;
  nickname: string;
  lines: SharedCartLine[];
  /** Sum of line quantities (cart badge count). */
  count: number;
}

/**
 * Read the guest_uuid claim from a table-guest JWT (header.payload.signature).
 * Display-only (no signature check); used to flag the current guest's own
 * lines in the shared cart. Returns '' on any malformed token.
 */
export function guestUuidFromToken(token: string): string {
  try {
    const payload = token.split('.')[1];
    if (!payload) return '';
    let b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const claims = JSON.parse(atob(b64)) as { guest_uuid?: string };
    return claims.guest_uuid ?? '';
  } catch {
    return '';
  }
}

/**
 * Subscribes to the existing 5s table-session poll (shared, deduped by RTK Query
 * with TablePanel's subscription) and surfaces the server-side shared cart.
 * Returns isShared=false outside shared dine-in; callers branch all shared UI on it.
 */
export function useSharedTableCart(): SharedTableCartData {
  const isShared = useAppSelector(selectIsSharedTableCart);
  const session = useAppSelector(selectTableSession);
  const guestToken = session?.guestToken ?? '';

  // Mirror TablePanel's visibility-gated polling so a hidden tab doesn't keep the
  // poll alive (and so this subscription doesn't override TablePanel's pause).
  const pollingInterval = useTableSessionPollInterval();

  const { data } = useGetTableSessionQuery(
    {
      restaurantUuid: session?.restaurantUuid ?? '',
      sessionUuid: session?.sessionUuid ?? '',
      guestToken,
    },
    {
      skip: !isShared || !guestToken,
      pollingInterval,
    },
  );

  const lines = data?.shared_cart?.lines ?? [];
  const count = lines.reduce((acc, line) => acc + (line.quantity || 0), 0);

  return {
    isShared,
    guestToken,
    guestUuid: guestUuidFromToken(guestToken),
    nickname: session?.nickname ?? '',
    lines,
    count,
  };
}
